import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Gamepad2, Coins, Trophy, Gift, Flame, LogIn, UserPlus, LogOut, User, Filter, Zap, Puzzle, Swords, Music } from 'lucide-react';
import { Particle } from '../items/Particle';
import { Toast } from '../items/Toast';
import { LevelUpModal } from '../components/LevelUpModal';
import { GameCard } from '../components/GameCard';
import { gamesData } from '../static/GamesConst';
import { useAuth } from '../context/AuthContext';
import { Leaderboard } from './Leaderboard';
import API_URL, { isProd } from '../config/APIURL';

export default function CugsGamesPortal() {
  const { user, isAuthenticated, logout, updateUser } = useAuth();
  
  // Use user data if logged in, otherwise use default values for display
  const [xp, setXp] = useState(user?.xp || 0);
  const [level, setLevel] = useState(user?.level || 1);
  const [coins, setCoins] = useState(user?.coins || 0);
  const streak = user?.streak || 0;
  const gamesPlayed = user?.gamesPlayed || 0;
  const totalXp = user?.totalXp || 0;
  
  const [filter, setFilter] = useState('all');
  const [toast, setToast] = useState({ show: false, message: '' });
  const [levelUp, setLevelUp] = useState(false);
  const [hoveredGame, setHoveredGame] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const [showLeaderboard, setShowLeaderboard] = useState(false);
  
  const [particles] = useState(() =>
    Array.from({ length: 15 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 15,
      duration: 10 + Math.random() * 10,
      color: ['#00f5ff', '#ff00ff', '#00ff88', '#ffff00'][Math.floor(Math.random() * 4)]
    }))
  );

  const xpForNextLevel = 1000;
  const xpProgress = (xp / xpForNextLevel) * 100;

  const showToast = (message) => {
    setToast({ show: true, message });
    setTimeout(() => setToast({ show: false, message: '' }), 3000);
  };

  const [bonusAvailable, setBonusAvailable] = useState(true);
  const [bonusTimeRemaining, setBonusTimeRemaining] = useState('');

  // Add this useEffect to check bonus status on mount
  useEffect(() => {
    if (isAuthenticated) {
      checkBonusStatus();
    }
  }, [isAuthenticated]);

  // Add this function
  const checkBonusStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/bonus-status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('cugs_token')}`
        }
      });
      const data = await response.json();
      setBonusAvailable(data.available);
      if (!data.available) {
        setBonusTimeRemaining(data.message);
      }
    } catch (error) {
      console.error('Error checking bonus status:', error);
    }
  };

  // Replace your existing handleDailyBonus function with this:
  const handleDailyBonus = async () => {
    if (!isAuthenticated) {
      showToast('🔒 Login to claim your daily bonus!');
      return;
    }
    
    if (!bonusAvailable) {
      showToast(`⏰ ${bonusTimeRemaining}`);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/user/claim-daily-bonus`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('cugs_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!data.success) {
        showToast(`⏰ ${data.message}`);
        setBonusAvailable(false);
        setBonusTimeRemaining(data.message);
        return;
      }
      
      // Update local state with server response
      setXp(data.user.xp);
      setLevel(data.user.level);
      setCoins(data.user.coins);
      
      if (data.leveledUp) {
        setLevelUp(true);
        setTimeout(() => setLevelUp(false), 3000);
      }
      
      showToast(`🎁 +${data.bonusAwarded.xp} XP & +${data.bonusAwarded.coins} Coins!`);
      
      // Mark bonus as unavailable and check status again
      setBonusAvailable(false);
      checkBonusStatus();
      
    } catch (error) {
      console.error('Error claiming bonus:', error);
      showToast('❌ Failed to claim bonus. Try again!');
    }
  };

  const [isProduction, setIsProduction] = useState(false); // Set to true in production

  const handlePlayGame = (game) => {
    if (!isAuthenticated) {
      showToast('🔒 Login to track your progress!');
    }
    
    showToast(`🚀 Launching ${game.title}...`);
    
    // Get token and pass it via URL
    const token = localStorage.getItem('cugs_token');
    const gameUrl = isProd 
      ? `https://${game.subdomain}.cugsgames.com${token ? `?token=${token}` : ''}`
      : `http://localhost:3001${token ? `?token=${token}` : ''}`;
    
    window.open(gameUrl, '_blank');
    
    if (isAuthenticated) {
      setTimeout(() => {
        let newXp = xp + 25;
        let newLevel = level;
        
        if (newXp >= xpForNextLevel) {
          newLevel = level + 1;
          newXp = newXp - xpForNextLevel;
          setLevelUp(true);
          setTimeout(() => setLevelUp(false), 3000);
        }
        
        setXp(newXp);
        setLevel(newLevel);
        
        updateUser({
          xp: newXp,
          level: newLevel,
          gamesPlayed: gamesPlayed + 1,
          totalXp: totalXp + 25
        });
      }, 1000);
    }
  };
  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    showToast('👋 See you next time, player!');
    // Reset local state
    setXp(0);
    setLevel(1);
    setCoins(0);
  };

  const filteredGames = filter === 'all' ? gamesData : gamesData.filter(g => g.category === filter);

  const categories = [
    { id: 'all', label: 'All', icon: Filter },
    { id: 'action', label: 'Action', icon: Zap },
    { id: 'puzzle', label: 'Puzzle', icon: Puzzle },
    { id: 'strategy', label: 'Strategy', icon: Swords },
    { id: 'music', label: 'Music', icon: Music }
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=Rajdhani:wght@300;400;500;600;700&display=swap');
        
        @keyframes float {
          0%, 100% { transform: translateY(100vh) translateX(0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(-100vh) translateX(100px); opacity: 0; }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes gridMove {
          0% { transform: perspective(500px) rotateX(60deg) translateY(0); }
          100% { transform: perspective(500px) rotateX(60deg) translateY(50px); }
        }
        
        @keyframes scanLine {
          0% { top: -10%; }
          100% { top: 110%; }
        }
        
        @keyframes fireFlicker {
          0% { transform: scale(1) rotate(-5deg); }
          100% { transform: scale(1.1) rotate(5deg); }
        }
        
        @keyframes logoGlow {
          0% { filter: drop-shadow(0 0 10px rgba(0, 245, 255, 0.5)); }
          100% { filter: drop-shadow(0 0 20px rgba(255, 0, 255, 0.5)); }
        }
        
        @keyframes pulseBorder {
          0%, 100% { border-color: #ff00ff; box-shadow: 0 0 10px rgba(255, 0, 255, 0.3); }
          50% { border-color: #00f5ff; box-shadow: 0 0 20px rgba(0, 245, 255, 0.3); }
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Rajdhani', sans-serif; }
      `}</style>

      <div style={{
        minHeight: '100vh',
        background: '#0a0a0f',
        color: '#fff',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated Grid Background */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: 'linear-gradient(rgba(0, 245, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 245, 255, 0.03) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
          animation: 'gridMove 20s linear infinite',
          pointerEvents: 'none',
          zIndex: 0
        }} />

        {/* Particles */}
        {particles.map(p => (
          <Particle
            key={p.id}
            style={{
              left: `${p.left}%`,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
              background: p.color,
              boxShadow: `0 0 10px ${p.color}, 0 0 20px ${p.color}`
            }}
          />
        ))}

        {/* Scan Line */}
        <div style={{
          position: 'fixed',
          height: '2px',
          width: '100%',
          left: 0,
          background: 'linear-gradient(90deg, transparent, #00f5ff, transparent)',
          animation: 'scanLine 8s linear infinite',
          pointerEvents: 'none',
          zIndex: 1
        }} />

        {/* Main Content */}
        <div style={{ position: 'relative', zIndex: 10 }}>
          {/* Header */}
          <header style={{
            padding: '20px 40px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid rgba(0, 245, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            position: 'sticky',
            top: 0,
            zIndex: 100,
            background: 'rgba(10, 10, 15, 0.8)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem', // Space between logo text and image
            }}>
              <img 
                src={`${process.env.PUBLIC_URL}/cug-games.png`}
                alt="HighMind Logo"
                style={{
                  width: '3.5rem',
                  height: '3.5rem',
                }}
              />
              <div style={{
                fontFamily: "'Orbitron', monospace",
                fontSize: '2rem',
                fontWeight: '900',
                background: 'linear-gradient(135deg, #00f5ff, #ff00ff)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '4px',
                animation: 'logoGlow 2s ease-in-out infinite alternate'
              }}>
                CUGS//GAMES
              </div>
            </div>

            {/* Conditional Header Content */}
            {isAuthenticated ? (
              /* Logged In - Show User Stats */
              <div style={{ display: 'flex', gap: '30px', alignItems: 'center' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 20px',
                  background: 'rgba(0, 245, 255, 0.05)',
                  border: '1px solid rgba(0, 245, 255, 0.2)',
                  borderRadius: '8px'
                }}>
                  <Coins size={24} color="#ffff00" />
                  <div>
                    <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '1.2rem', fontWeight: '700', color: '#00f5ff' }}>
                      {coins.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                      Coins
                    </div>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 20px',
                  background: 'rgba(0, 245, 255, 0.05)',
                  border: '1px solid rgba(0, 245, 255, 0.2)',
                  borderRadius: '8px'
                }}>
                  <Trophy size={24} color="#ff00ff" />
                  <div>
                    <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '1.2rem', fontWeight: '700', color: '#00f5ff' }}>
                      LVL {level}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                      Rank
                    </div>
                  </div>
                </div>

                <div style={{ width: '200px' }}>
                  <div style={{
                    height: '8px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${xpProgress}%`,
                      background: 'linear-gradient(90deg, #00ff88, #00f5ff)',
                      borderRadius: '4px',
                      transition: 'width 0.5s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                        animation: 'shimmer 2s infinite'
                      }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                    <span>{xp} XP</span>
                    <span>{xpForNextLevel} XP</span>
                  </div>
                </div>

                {/* User Menu */}
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 20px',
                      background: 'linear-gradient(135deg, rgba(0, 245, 255, 0.1), rgba(255, 0, 255, 0.1))',
                      border: '1px solid rgba(255, 0, 255, 0.3)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <User size={20} color="#ff00ff" />
                    <span style={{ 
                      fontFamily: "'Orbitron', monospace", 
                      fontSize: '0.9rem', 
                      fontWeight: '600',
                      color: '#fff'
                    }}>
                      {user?.username}
                    </span>
                  </button>
                  
                  {showUserMenu && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '10px',
                      background: 'rgba(10, 10, 15, 0.95)',
                      border: '1px solid rgba(255, 0, 255, 0.3)',
                      borderRadius: '10px',
                      padding: '10px',
                      minWidth: '180px',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <button
                        onClick={handleLogout}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          width: '100%',
                          padding: '12px 15px',
                          background: 'transparent',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#ff3232',
                          fontSize: '0.95rem',
                          fontFamily: "'Rajdhani', sans-serif",
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => e.target.style.background = 'rgba(255, 50, 50, 0.1)'}
                        onMouseOut={(e) => e.target.style.background = 'transparent'}
                      >
                        <LogOut size={18} />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Not Logged In - Show Login/Signup Buttons */
              <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                <Link to="/login" style={{ textDecoration: 'none' }}>
                  <button
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 24px',
                      background: 'transparent',
                      border: '1px solid rgba(0, 245, 255, 0.5)',
                      borderRadius: '8px',
                      color: '#00f5ff',
                      fontSize: '1rem',
                      fontFamily: "'Orbitron', monospace",
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = 'rgba(0, 245, 255, 0.1)';
                      e.target.style.boxShadow = '0 0 20px rgba(0, 245, 255, 0.3)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = 'transparent';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <LogIn size={18} />
                    Login
                  </button>
                </Link>
                
                <Link to="/signup" style={{ textDecoration: 'none' }}>
                  <button
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 24px',
                      background: 'linear-gradient(135deg, #00f5ff, #ff00ff)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#000',
                      fontSize: '1rem',
                      fontFamily: "'Orbitron', monospace",
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 10px 30px rgba(255, 0, 255, 0.3)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <UserPlus size={18} />
                    Sign Up
                  </button>
                </Link>
              </div>
            )}
          </header>

          {/* Hero Section */}
          <section style={{ padding: '80px 40px', textAlign: 'center' }}>
            <h1 style={{
              fontFamily: "'Orbitron', monospace",
              fontSize: '4rem',
              fontWeight: '900',
              marginBottom: '20px',
              textTransform: 'uppercase',
              letterSpacing: '8px'
            }}>
              {'CUGS GAMES'.split('').map((char, i) => (
                <span
                  key={i}
                  style={{
                    display: char === ' ' ? 'inline' : 'inline-block',
                    color: i % 2 === 0 ? '#00f5ff' : '#ff00ff',
                    textShadow: i % 2 === 0 ? '0 0 20px #00f5ff, 0 0 40px #00f5ff' : '0 0 20px #ff00ff, 0 0 40px #ff00ff'
                  }}
                >
                  {char === ' ' ? '\u00A0' : char}
                </span>
              ))}
            </h1>
            <p style={{
              fontSize: '1.5rem',
              color: 'rgba(255, 255, 255, 0.6)',
              marginBottom: '40px',
              letterSpacing: '4px'
            }}>
              INSTANT PLAY • NO DOWNLOADS • PURE FUN
            </p>

            <div
              onClick={handleDailyBonus}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '15px',
                padding: '15px 30px',
                background: bonusAvailable 
                  ? 'linear-gradient(135deg, rgba(255, 0, 255, 0.2), rgba(0, 245, 255, 0.2))'
                  : 'rgba(100, 100, 100, 0.2)',
                border: bonusAvailable ? '2px solid #ff00ff' : '2px solid rgba(150, 150, 150, 0.5)',
                borderRadius: '50px',
                animation: bonusAvailable ? 'pulseBorder 2s ease-in-out infinite' : 'none',
                cursor: bonusAvailable ? 'pointer' : 'not-allowed',
                opacity: bonusAvailable ? 1 : 0.6,
                transition: 'all 0.3s ease'
              }}
            >
              <Gift size={32} color="#ffff00" style={{ animation: bonusAvailable ? 'pulse 1s ease-in-out infinite' : 'none' }} />
              <div>
                <div style={{ fontFamily: "'Orbitron', monospace", fontWeight: '700', fontSize: '1.1rem' }}>
                  {bonusAvailable ? 'DAILY BONUS' : 'BONUS CLAIMED'}
                </div>
                <div style={{ color: '#ffff00', fontSize: '1rem', fontFamily: "'Orbitron', monospace" }}>
                  {bonusAvailable ? '+100 XP & +500 COINS' : bonusTimeRemaining}
                </div>
              </div>
            </div>
          </section>
          {/* Streak Section */}
          <section style={{
            padding: '40px',
            margin: '0 40px',
            background: 'linear-gradient(135deg, rgba(255, 102, 0, 0.1), rgba(255, 0, 255, 0.1))',
            borderRadius: '20px',
            border: '1px solid rgba(255, 102, 0, 0.3)',
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '30px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <Flame size={64} color="#ff6600" style={{ animation: 'fireFlicker 0.5s ease-in-out infinite alternate' }} />
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontFamily: "'Orbitron', monospace",
                fontSize: '3rem',
                fontWeight: '900',
                background: 'linear-gradient(135deg, #ff6600, #ffff00)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                {isAuthenticated ? streak : '?'}
              </div>
              <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '2px', marginTop: '5px' }}>
                Day Streak
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontFamily: "'Orbitron', monospace",
                fontSize: '3rem',
                fontWeight: '900',
                background: 'linear-gradient(135deg, #ff6600, #ffff00)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                {isAuthenticated ? gamesPlayed : '?'}
              </div>
              <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '2px', marginTop: '5px' }}>
                Games Played
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontFamily: "'Orbitron', monospace",
                fontSize: '3rem',
                fontWeight: '900',
                background: 'linear-gradient(135deg, #ff6600, #ffff00)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                {isAuthenticated ? (totalXp >= 1000 ? `${(totalXp / 1000).toFixed(1)}K` : totalXp) : '?'}
              </div>
              <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '2px', marginTop: '5px' }}>
                Total XP
              </div>
            </div>
          </section>

          {/* Games Section */}
          <section style={{ padding: '40px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '40px',
              flexWrap: 'wrap',
              gap: '20px'
            }}>
              {/* Left side - Title and Leaderboards button together */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <h2 style={{
                  fontFamily: "'Orbitron', monospace",
                  fontSize: '2rem',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px'
                }}>
                  <span style={{
                    width: '5px',
                    height: '40px',
                    background: 'linear-gradient(180deg, #00f5ff, #ff00ff)',
                    borderRadius: '3px',
                    display: 'inline-block'
                  }} />
                  CHOOSE YOUR GAME
                </h2>
                
                <Link to="/leaderboards" style={{ textDecoration: 'none' }}>
                  <button
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      background: 'transparent',
                      border: '1px solid rgba(255, 215, 0, 0.4)',
                      borderRadius: '8px',
                      color: '#FFD700',
                      fontFamily: "'Orbitron', monospace",
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 215, 0, 0.1)';
                      e.currentTarget.style.boxShadow = '0 0 15px rgba(255, 215, 0, 0.3)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <Trophy size={18} />
                    Leaderboards
                  </button>
                </Link>
              </div>

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {categories.map(cat => {
                  const Icon = cat.icon;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setFilter(cat.id)}
                      style={{
                        padding: '10px 20px',
                        background: filter === cat.id ? 'linear-gradient(135deg, #00f5ff, #ff00ff)' : 'transparent',
                        border: filter === cat.id ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '25px',
                        color: filter === cat.id ? '#000' : 'rgba(255, 255, 255, 0.6)',
                        fontFamily: "'Rajdhani', sans-serif",
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <Icon size={16} />
                      {cat.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '30px'
            }}>
              {filteredGames.map(game => (
                <GameCard
                  key={game.id}
                  game={game}
                  onPlay={handlePlayGame}
                  isHovered={hoveredGame === game.id}
                  onHover={setHoveredGame}
                />
              ))}
            </div>
          </section>

          {/* Footer */}
          <footer style={{
            padding: '40px',
            textAlign: 'center',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            marginTop: '60px'
          }}>
            <p style={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '0.9rem' }}>
              © 2025 CUGS GAMES • Built for fun, played for glory
            </p>
          </footer>
        </div>

        <Toast show={toast.show} message={toast.message} />
        <LevelUpModal show={levelUp} level={level} onClose={() => setLevelUp(false)} />
      </div>
    </>
  );

}
